import { t } from 'i18next';
import _ from 'lodash';
import { FC, useState } from 'react';
import { ErrorBoundary } from '../../../../../../../shared/frontend/components/errorBoundary';
import { Col } from '../../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../../shared/frontend/components/grid/Row';
import { findClassBySlug } from '../../../../../../../shared/frontend/libs/siteMeta';
import { TAction } from '../../../../../../../shared/frontend/scripts/actions/types/action';
import {
  allRegionsSelector,
  classesSelector,
  useClassBySlug,
  useRegionBySlug
} from '../../../../../../../shared/frontend/scripts/selectors/global';
import { namedRoutes } from '../../../bundles/routes';
import { ClassSelect } from '../../../../../../../shared/frontend/scripts/wow/components/controls/ClassSelect';
import { TopAchievementsCharactersTable } from '../../../GlobalSearch/components/Dynamic/TopAchievementsCharactersTable';
import { TopILvlCharactersTable } from '../../../GlobalSearch/components/Dynamic/TopILvlCharactersTable';
import { TopILvlGuildsTable } from '../../../GlobalSearch/components/Dynamic/TopILvlGuildsTable';
import {
  generateDynamicGlobalSearchPreloadActions,
  useDynamicGlobalSearch
} from '../../../GlobalSearch/hooks/use-dynamic-global-search';
import {
  generateTopAchievementsCharactersParams,
  generateTopIlvlCharactersParams,
  generateTopIlvlGuildsParams
} from '../../../GlobalSearch/utils/generateSearchParams';
import frontendLog from '../../../libs/frontendLog';
import { IDataTableProps } from '../../../../../../../shared/frontend/components/tables/types';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { TClassParams } from '../homeContent';
import { HomePageSection } from './ui/HomePageSection';

export interface ITopILvlAndAchievementsSectionProps {
  enabled: boolean;
  region?: string;
  realm?: string;
  limit?: number;
  initialClassParams?: TClassParams;
}

export const TopILvlAndAchievementsSection: FC<ITopILvlAndAchievementsSectionProps> & {
  preloadComponentActions: (props: ITopILvlAndAchievementsSectionProps, params: any) => Promise<TAction<any>[]>;
} = ({ region: regionSlug, realm: realmSlug, limit, initialClassParams }) => {
  const [characterItemLevelClassSpecParams, setCharacterItemLevelClassSpecParams] = useState<TClassParams | null>(
    initialClassParams || null
  );

  const currentClassForItemLevel = useClassBySlug(characterItemLevelClassSpecParams?.classSlug);

  const region = useRegionBySlug(regionSlug);
  const realm = realmSlug ? { slug: realmSlug } : undefined;

  const topIlvlCharactersParams = generateTopIlvlCharactersParams({
    region,
    realm,
    limit,
    classId: currentClassForItemLevel?.id
  });

  const topIlvlGuildsParams = generateTopIlvlGuildsParams({ region, realm, limit, includeLogo: true });
  const topAchievementsCharactersParams = generateTopAchievementsCharactersParams({
    region,
    realm,
    limit
  });

  const topILvlCharactersState = useDynamicGlobalSearch(topIlvlCharactersParams);
  const topILvlGuildsState = useDynamicGlobalSearch(topIlvlGuildsParams);
  const topAchievementsCharactersState = useDynamicGlobalSearch(topAchievementsCharactersParams);

  const generateLinkToSearch = (params: Record<string, any>) => {
    return `${namedRoutes.Search}?${new URLSearchParams(params).toString()}`;
  };

  const tableProps: Partial<IDataTableProps<any>> = {
    style: {
      tableLayout: 'fixed'
    },
    wrapperProps: {
      className: 'h-full'
    },
    className: 'h-full',
    containerClassName: 'h-full',
    emptyContent: t('common:no-data')
  };

  return (
    <ErrorBoundary fallback={<></>}>
      <section>
        <h2 className="slds-m-bottom--small slds-m-top--large">
          <span className="rio-text-title--caps text-raiderio">
            {t('frontpage:section-header-top-item-level-and-achievements')}
          </span>
        </h2>

        <Row gutter={['xx-small', 'xx-small']}>
          <Col lg={4} sm={6} xs={12}>
            <HomePageSection
              title={t('frontpage:character-item-level')}
              footer={
                <Link className="rio-realm-link--muted" to={generateLinkToSearch(topIlvlCharactersParams)}>
                  {t('common:view-all')}
                </Link>
              }
              rightHeaderContent={
                <ClassSelect
                  emptyOption={{
                    enabled: true,
                    label: t('common:all-classes')
                  }}
                  value={currentClassForItemLevel?.slug || null}
                  getOptionValue={(option) => option.slug}
                  onChange={(slug) => {
                    setCharacterItemLevelClassSpecParams(
                      slug
                        ? {
                            classSlug: slug
                          }
                        : null
                    );
                  }}
                  isClearable={false}
                  size="xx-small"
                  variant="borderless"
                  isSearchable={false}
                  viewOptions={{
                    classColor: true
                  }}
                  valueColor="raiderio"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      minWidth: 200,
                      position: 'absolute',
                      right: 0
                    })
                  }}
                />
              }
            >
              <TopILvlCharactersTable
                data={topILvlCharactersState.data?.matches || []}
                isLoading={!topILvlCharactersState.loaded}
                {...tableProps}
              />
            </HomePageSection>
          </Col>
          <Col lg={4} sm={6} xs={12}>
            <HomePageSection
              title={t('frontpage:guild-item-level')}
              footer={
                <Link className="rio-realm-link--muted" to={generateLinkToSearch(topIlvlGuildsParams)}>
                  {t('common:view-all')}
                </Link>
              }
            >
              <TopILvlGuildsTable
                data={topILvlGuildsState.data?.matches || []}
                isLoading={!topILvlGuildsState.loaded}
                {...tableProps}
              />
            </HomePageSection>
          </Col>
          <Col lg={4} xs={12}>
            <HomePageSection
              title={t('frontpage:achievement-points')}
              footer={
                <Link className="rio-realm-link--muted" to={generateLinkToSearch(topAchievementsCharactersParams)}>
                  {t('common:view-all')}
                </Link>
              }
            >
              <TopAchievementsCharactersTable
                data={topAchievementsCharactersState.data?.matches || []}
                isLoading={!topAchievementsCharactersState.loaded}
                {...tableProps}
              />
            </HomePageSection>
          </Col>
        </Row>
      </section>
    </ErrorBoundary>
  );
};

TopILvlAndAchievementsSection.preloadComponentActions = async (props, params): Promise<TAction<any>[]> => {
  const actions: TAction<any>[] = [];
  const { state } = params;

  const regions = allRegionsSelector(state);
  const region = regions.find((r) => r.slug === props.region);

  const baseParams = {
    region,
    realm: props.realm ? { slug: props.realm } : undefined,
    limit: props.limit
  };

  let classId: number | undefined;

  if (props.initialClassParams) {
    const classes = classesSelector(state);

    const cls = findClassBySlug(classes, props.initialClassParams.classSlug);

    if (cls) {
      classId = cls.id;
    }
  }

  const topIlvlCharactersParams = generateTopIlvlCharactersParams({ ...baseParams, classId });
  const topIlvlGuildsParams = generateTopIlvlGuildsParams(baseParams);
  const topAchievementsCharactersParams = generateTopAchievementsCharactersParams(baseParams);

  try {
    const data = await Promise.all([
      generateDynamicGlobalSearchPreloadActions(topIlvlCharactersParams),
      generateDynamicGlobalSearchPreloadActions(topIlvlGuildsParams),
      generateDynamicGlobalSearchPreloadActions(topAchievementsCharactersParams)
    ]);

    actions.push(...data.flat());
  } catch (error) {
    frontendLog.error({ error }, 'TopILvlSection.preloadComponentActions failed');
  }

  return actions;
};
